import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
  X,
  Eye,
  EyeOff,
  Clock,
  User,
  Trash2,
  Filter,
  Search,
  Bell,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RefreshCw,
  CheckCheck,
  Zap,
  TrendingDown,
  Droplets,
  Wrench,
  MapPin,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { db } from '../utils/firebase/client';
import { collection, query, orderBy, onSnapshot, updateDoc, doc, Timestamp, writeBatch, where } from 'firebase/firestore';
import { getCompanyId } from '../utils/tenantContext';
import { toast } from 'sonner';
import { cn } from './ui/utils';

// ============================================================================
// TYPES
// ============================================================================

interface AlertData {
  id: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  title: string;
  message: string;
  wellId: string;
  wellName: string;
  status: 'new' | 'acknowledged' | 'in_progress' | 'resolved' | 'closed' | 'dismissed';
  created_at: any;
  threshold_value?: number;
  actual_value?: number;
  difference_pct?: number;
  unit?: string;
  suggested_actions?: string[];
  acknowledged_by_name?: string;
  acknowledged_at?: any;
  resolved_by_name?: string;
  resolved_at?: any;
  enteredByName?: string;
  viewed_by?: string[];
  view_count?: number;
}

interface AlertCenterProps {
  onClose: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function AlertCenter({ onClose }: AlertCenterProps) {
  const [alerts, setAlerts] = useState<AlertData[]>([]);
  const [filter, setFilter] = useState<'all' | 'new' | 'acknowledged' | 'resolved' | 'dismissed'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [selectedAlert, setSelectedAlert] = useState<AlertData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acknowledgeNotes, setAcknowledgeNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const companyId = getCompanyId();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Stats
  const stats = {
    total: alerts.length,
    new: alerts.filter(a => a.status === 'new').length,
    critical: alerts.filter(a => a.severity === 'critical' && a.status !== 'resolved' && a.status !== 'dismissed').length,
    high: alerts.filter(a => a.severity === 'high' && a.status !== 'resolved' && a.status !== 'dismissed').length,
    acknowledged: alerts.filter(a => a.status === 'acknowledged').length,
    resolved: alerts.filter(a => a.status === 'resolved').length,
  };

  // Load alerts from Firebase
  useEffect(() => {
    if (!companyId) return;


    let q;
    // 🔒 SECURITY: Filter by recipient to match Firestore rules
    if (currentUser.role === 'superadmin' || currentUser.isSuperAdmin === true) {
      q = query(
        collection(db, `companies/${companyId}/alerts`),
        orderBy('created_at', 'desc')
      );
    } else {
      q = query(
        collection(db, `companies/${companyId}/alerts`),
        where('recipientIds', 'array-contains', currentUser.id || currentUser.uid),
        orderBy('created_at', 'desc')
      );
    }

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const alertsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AlertData[];

        setAlerts(alertsData);
        setLoading(false);
        setRefreshing(false);
      },
      (error) => {
        console.error('❌ Error loading alerts:', error);
        toast.error('Failed to load alerts');
        setLoading(false);
        setRefreshing(false);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Filtered alerts
  const filteredAlerts = alerts.filter(alert => {
    // Status filter
    if (filter !== 'all' && alert.status !== filter) return false;

    // Severity filter
    if (severityFilter !== 'all' && alert.severity !== severityFilter) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        alert.title.toLowerCase().includes(query) ||
        alert.message.toLowerCase().includes(query) ||
        alert.wellName?.toLowerCase().includes(query) ||
        alert.category?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // ============================================================================
  // UI HELPERS
  // ============================================================================

  const getSeverityConfig = (severity: string) => {
    const configs = {
      critical: {
        icon: <AlertCircle className="h-5 w-5" />,
        color: 'text-red-500',
        bg: 'bg-red-500/10 dark:bg-red-500/20',
        border: 'border-red-500/30',
        badge: 'bg-red-500 text-white',
        glow: 'shadow-red-500/20',
      },
      high: {
        icon: <AlertTriangle className="h-5 w-5" />,
        color: 'text-orange-500',
        bg: 'bg-orange-500/10 dark:bg-orange-500/20',
        border: 'border-orange-500/30',
        badge: 'bg-orange-500 text-white',
        glow: 'shadow-orange-500/20',
      },
      medium: {
        icon: <Info className="h-5 w-5" />,
        color: 'text-yellow-500',
        bg: 'bg-yellow-500/10 dark:bg-yellow-500/20',
        border: 'border-yellow-500/30',
        badge: 'bg-yellow-500 text-white',
        glow: 'shadow-yellow-500/20',
      },
      low: {
        icon: <Info className="h-5 w-5" />,
        color: 'text-blue-500',
        bg: 'bg-blue-500/10 dark:bg-blue-500/20',
        border: 'border-blue-500/30',
        badge: 'bg-blue-500 text-white',
        glow: 'shadow-blue-500/20',
      },
    };
    return configs[severity as keyof typeof configs] || configs.medium;
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      new: { label: 'New', color: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700', dot: 'bg-red-500' },
      acknowledged: { label: 'Acknowledged', color: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700', dot: 'bg-yellow-500' },
      in_progress: { label: 'In Progress', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 border-blue-300 dark:border-blue-700', dot: 'bg-blue-500' },
      resolved: { label: 'Resolved', color: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200 border-emerald-300 dark:border-emerald-700', dot: 'bg-emerald-500' },
      dismissed: { label: 'Dismissed', color: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600', dot: 'bg-slate-400' },
    };
    return configs[status as keyof typeof configs] || configs.new;
  };

  const getAlertTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      'production-drop': <TrendingDown className="h-4 w-4" />,
      'tank-level': <Droplets className="h-4 w-4" />,
      'pressure-drop': <Zap className="h-4 w-4" />,
      'maintenance-due': <Wrench className="h-4 w-4" />,
      'location': <MapPin className="h-4 w-4" />,
    };
    return icons[type] || <AlertCircle className="h-4 w-4" />;
  };

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return 'Unknown';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleAcknowledge = async (alert: AlertData, notes?: string) => {
    try {
      const alertRef = doc(db, `companies/${companyId}/alerts/${alert.id}`);

      await updateDoc(alertRef, {
        status: 'acknowledged',
        acknowledged_at: Timestamp.now(),
        acknowledged_by: currentUser.uid || 'unknown',
        acknowledged_by_name: currentUser.name || currentUser.email || 'User',
        acknowledgment_notes: notes || acknowledgeNotes || null,
        updatedAt: Timestamp.now(),
        viewed_by: [...(alert.viewed_by || []), currentUser.uid],
        view_count: (alert.view_count || 0) + 1,
      });

      toast.success('Alert acknowledged');
      setSelectedAlert(null);
      setAcknowledgeNotes('');
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleDismiss = async (alert: AlertData) => {
    try {
      const alertRef = doc(db, `companies/${companyId}/alerts/${alert.id}`);

      await updateDoc(alertRef, {
        status: 'dismissed',
        dismissed_at: Timestamp.now(),
        dismissed_by: currentUser.uid || 'unknown',
        updatedAt: Timestamp.now(),
      });

      toast.success('Alert dismissed');
      setSelectedAlert(null);
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  const handleResolve = async (alert: AlertData) => {
    try {
      const alertRef = doc(db, `companies/${companyId}/alerts/${alert.id}`);

      await updateDoc(alertRef, {
        status: 'resolved',
        resolved_at: Timestamp.now(),
        resolved_by: currentUser.uid || 'unknown',
        resolved_by_name: currentUser.name || currentUser.email || 'User',
        updatedAt: Timestamp.now(),
      });

      toast.success('Alert resolved');
      setSelectedAlert(null);
    } catch (error) {
      console.error('Error resolving alert:', error);
      toast.error('Failed to resolve alert');
    }
  };

  const handleAcknowledgeAll = async () => {
    const newAlerts = alerts.filter(a => a.status === 'new');
    if (newAlerts.length === 0) return;

    try {
      const batch = writeBatch(db);

      newAlerts.forEach(alert => {
        const alertRef = doc(db, `companies/${companyId}/alerts/${alert.id}`);
        batch.update(alertRef, {
          status: 'acknowledged',
          acknowledged_at: Timestamp.now(),
          acknowledged_by: currentUser.uid || 'unknown',
          acknowledged_by_name: currentUser.name || currentUser.email || 'User',
          updatedAt: Timestamp.now(),
        });
      });

      await batch.commit();
      toast.success(`${newAlerts.length} alerts acknowledged`);
    } catch (error) {
      console.error('Error acknowledging all alerts:', error);
      toast.error('Failed to acknowledge alerts');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    // The useEffect will handle the refresh via the listener
    setTimeout(() => setRefreshing(false), 1000);
  };

  // ============================================================================
  // RENDER: LOADING STATE
  // ============================================================================

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="relative mx-auto w-16 h-16 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-slate-200 dark:border-slate-700"></div>
              <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 animate-spin"></div>
              <Bell className="absolute inset-0 m-auto h-6 w-6 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Loading Alerts</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Fetching latest alerts from server...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // RENDER: ALERT DETAIL VIEW
  // ============================================================================

  if (selectedAlert) {
    const severityConfig = getSeverityConfig(selectedAlert.severity);
    const statusConfig = getStatusConfig(selectedAlert.status);

    return (
      <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex flex-col">
        {/* Scrollable container */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="min-h-full flex flex-col p-3 sm:p-6 pb-[env(safe-area-inset-bottom,24px)]">
            <Card className="w-full max-w-2xl mx-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col">
              {/* Header */}
              <div className={cn(
                "p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700",
                severityConfig.bg
              )}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={cn(
                      "p-2.5 rounded-xl shrink-0",
                      severityConfig.bg,
                      severityConfig.border,
                      "border-2"
                    )}>
                      <span className={severityConfig.color}>{severityConfig.icon}</span>
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white leading-tight">
                        {selectedAlert.title}
                      </h2>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge className={cn(severityConfig.badge, "text-xs font-bold")}>
                          {selectedAlert.severity.toUpperCase()}
                        </Badge>
                        <Badge className={cn(statusConfig.color, "text-xs font-medium border")}>
                          <span className={cn("w-1.5 h-1.5 rounded-full mr-1.5", statusConfig.dot)}></span>
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedAlert(null)}
                    className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full h-10 w-10"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 space-y-6 flex-1">
                {/* Well & Time Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs uppercase font-medium mb-1">
                      <MapPin className="h-3.5 w-3.5" />
                      Well
                    </div>
                    <p className="font-semibold text-slate-900 dark:text-white truncate">{selectedAlert.wellName}</p>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs uppercase font-medium mb-1">
                      <Clock className="h-3.5 w-3.5" />
                      Created
                    </div>
                    <p className="font-semibold text-slate-900 dark:text-white">{formatTimeAgo(selectedAlert.created_at)}</p>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                    What Happened
                  </h3>
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                    {selectedAlert.message}
                  </p>
                </div>

                {/* Metrics (if available) */}
                {selectedAlert.threshold_value !== undefined && (
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-center border border-slate-200 dark:border-slate-700">
                      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Expected</div>
                      <div className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                        {selectedAlert.threshold_value.toFixed(1)}
                      </div>
                      <div className="text-xs text-slate-400">{selectedAlert.unit}</div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-3 text-center border border-red-200 dark:border-red-800">
                      <div className="text-xs text-red-600 dark:text-red-400 mb-1">Actual</div>
                      <div className="text-lg sm:text-xl font-bold text-red-600 dark:text-red-400">
                        {selectedAlert.actual_value?.toFixed(1)}
                      </div>
                      <div className="text-xs text-red-400">{selectedAlert.unit}</div>
                    </div>
                    {selectedAlert.difference_pct !== undefined && (
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-3 text-center border border-orange-200 dark:border-orange-800">
                        <div className="text-xs text-orange-600 dark:text-orange-400 mb-1">Difference</div>
                        <div className="text-lg sm:text-xl font-bold text-orange-600 dark:text-orange-400">
                          {selectedAlert.difference_pct.toFixed(0)}%
                        </div>
                        <div className="text-xs text-orange-400">variance</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Suggested Actions */}
                {selectedAlert.suggested_actions && selectedAlert.suggested_actions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-3">
                      Recommended Actions
                    </h3>
                    <div className="space-y-2">
                      {selectedAlert.suggested_actions.map((action, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800"
                        >
                          <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                          <span className="text-sm text-emerald-800 dark:text-emerald-200">{action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Acknowledgment history */}
                {selectedAlert.acknowledged_by_name && (
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <Eye className="h-5 w-5 text-yellow-500" />
                    <div className="text-sm">
                      <span className="text-yellow-700 dark:text-yellow-300">Acknowledged by </span>
                      <span className="font-semibold text-yellow-800 dark:text-yellow-200">{selectedAlert.acknowledged_by_name}</span>
                      {selectedAlert.acknowledged_at && (
                        <span className="text-yellow-600 dark:text-yellow-400"> • {formatTimeAgo(selectedAlert.acknowledged_at)}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes input for acknowledgment */}
                {selectedAlert.status === 'new' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Add Notes (optional)
                    </label>
                    <textarea
                      value={acknowledgeNotes}
                      onChange={(e) => setAcknowledgeNotes(e.target.value)}
                      placeholder="What actions did you take? Any observations?"
                      className="w-full px-4 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows={3}
                    />
                  </div>
                )}
              </div>

              {/* Actions Footer */}
              <div className="p-4 sm:p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setSelectedAlert(null)}
                    className="flex-1 h-12 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Close
                  </Button>

                  {selectedAlert.status === 'new' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleDismiss(selectedAlert)}
                        className="flex-1 h-12 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Dismiss
                      </Button>
                      <Button
                        onClick={() => handleAcknowledge(selectedAlert)}
                        className="flex-1 h-12 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Acknowledge
                      </Button>
                    </>
                  )}

                  {selectedAlert.status === 'acknowledged' && (
                    <Button
                      onClick={() => handleResolve(selectedAlert)}
                      className="flex-1 h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark Resolved
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: MAIN ALERT LIST
  // ============================================================================

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex flex-col">
      {/* Main Scrollable Container */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="min-h-full flex flex-col p-2 sm:p-4 pb-[env(safe-area-inset-bottom,16px)]">
          <Card className="w-full max-w-4xl mx-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col overflow-hidden">

            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              {/* Title Row */}
              <div className="p-4 sm:p-6 pb-0 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl shadow-lg">
                      <Bell className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                        Alert Center
                      </h2>
                      <p className="text-sm text-slate-500 dark:text-slate-400 hidden sm:block">
                        Monitor and manage system alerts
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full h-10 w-10"
                  >
                    <RefreshCw className={cn("h-5 w-5", refreshing && "animate-spin")} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full h-10 w-10"
                  >
                    {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full h-10 w-10"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Stats Row */}
              <div className="px-4 sm:px-6 py-3 flex items-center gap-2 overflow-x-auto hide-scrollbar">
                <Badge
                  className={cn(
                    "shrink-0 px-3 py-1.5 text-sm font-semibold cursor-pointer transition-all",
                    stats.new > 0 ? "bg-red-500 text-white animate-pulse" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                  )}
                  onClick={() => setFilter('new')}
                >
                  {stats.new} New
                </Badge>
                {stats.critical > 0 && (
                  <Badge
                    className="shrink-0 px-3 py-1.5 text-sm font-semibold bg-red-600 text-white cursor-pointer"
                    onClick={() => setSeverityFilter('critical')}
                  >
                    {stats.critical} Critical
                  </Badge>
                )}
                {stats.high > 0 && (
                  <Badge
                    className="shrink-0 px-3 py-1.5 text-sm font-semibold bg-orange-500 text-white cursor-pointer"
                    onClick={() => setSeverityFilter('high')}
                  >
                    {stats.high} High
                  </Badge>
                )}
                <Badge
                  className="shrink-0 px-3 py-1.5 text-sm font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 cursor-pointer"
                  onClick={() => setFilter('acknowledged')}
                >
                  {stats.acknowledged} Ack'd
                </Badge>
                <Badge
                  className="shrink-0 px-3 py-1.5 text-sm font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 cursor-pointer"
                  onClick={() => setFilter('resolved')}
                >
                  {stats.resolved} Resolved
                </Badge>
              </div>

              {/* Search & Filters */}
              <div className="px-4 sm:px-6 pb-4 space-y-3">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search alerts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Filter Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  <Filter className="h-4 w-4" />
                  <span>Filters</span>
                  {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>

                {/* Filter Pills */}
                {showFilters && (
                  <div className="flex flex-wrap gap-2 animate-in slide-in-from-top-2">
                    {/* Status Filters */}
                    <div className="w-full text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold mb-1">Status</div>
                    {['all', 'new', 'acknowledged', 'resolved', 'dismissed'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setFilter(status as any)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                          filter === status
                            ? "bg-blue-500 text-white shadow-lg shadow-blue-500/25"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                        )}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}

                    {/* Severity Filters */}
                    <div className="w-full text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold mb-1 mt-2">Severity</div>
                    {['all', 'critical', 'high', 'medium', 'low'].map((severity) => (
                      <button
                        key={severity}
                        onClick={() => setSeverityFilter(severity as any)}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-sm font-medium transition-all",
                          severityFilter === severity
                            ? severity === 'critical' ? "bg-red-500 text-white" :
                              severity === 'high' ? "bg-orange-500 text-white" :
                                severity === 'medium' ? "bg-yellow-500 text-white" :
                                  severity === 'low' ? "bg-blue-500 text-white" :
                                    "bg-slate-700 text-white"
                            : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                        )}
                      >
                        {severity.charAt(0).toUpperCase() + severity.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              {stats.new > 0 && (
                <div className="px-4 sm:px-6 pb-4">
                  <Button
                    onClick={handleAcknowledgeAll}
                    variant="outline"
                    className="w-full h-10 text-yellow-600 dark:text-yellow-400 border-yellow-300 dark:border-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                  >
                    <CheckCheck className="h-4 w-4 mr-2" />
                    Acknowledge All ({stats.new})
                  </Button>
                </div>
              )}
            </div>

            {/* Alert List */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3">
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-emerald-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                    {searchQuery ? 'No Matching Alerts' : 'All Clear!'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                    {searchQuery
                      ? `No alerts match "${searchQuery}"`
                      : filter === 'all'
                        ? 'No alerts to display. Everything is running smoothly.'
                        : `No ${filter} alerts at this time.`}
                  </p>
                  {(filter !== 'all' || severityFilter !== 'all' || searchQuery) && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilter('all');
                        setSeverityFilter('all');
                        setSearchQuery('');
                      }}
                      className="mt-4"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : (
                filteredAlerts.map((alert) => {
                  const severityConfig = getSeverityConfig(alert.severity);
                  const statusConfig = getStatusConfig(alert.status);
                  const isNew = alert.status === 'new';

                  return (
                    <div
                      key={alert.id}
                      onClick={() => setSelectedAlert(alert)}
                      className={cn(
                        "relative p-4 rounded-xl border-2 cursor-pointer transition-all group",
                        "hover:shadow-lg hover:scale-[1.01]",
                        severityConfig.bg,
                        severityConfig.border,
                        isNew && "ring-2 ring-red-500/20"
                      )}
                    >
                      {/* New indicator */}
                      {isNew && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse shadow-lg shadow-red-500/50" />
                      )}

                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={cn(
                          "p-2 rounded-lg shrink-0",
                          severityConfig.bg,
                          severityConfig.border,
                          "border"
                        )}>
                          <span className={severityConfig.color}>{getAlertTypeIcon(alert.type)}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                              {alert.title}
                            </h3>
                            <Badge className={cn(statusConfig.color, "shrink-0 text-[10px] font-medium border")}>
                              {statusConfig.label}
                            </Badge>
                          </div>

                          <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 mb-2">
                            {alert.message}
                          </p>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {alert.wellName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimeAgo(alert.created_at)}
                            </span>
                            {alert.enteredByName && (
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {alert.enteredByName}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Quick action & chevron */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Badge className={cn(severityConfig.badge, "text-[10px] font-bold hidden sm:flex")}>
                            {alert.severity.toUpperCase()}
                          </Badge>
                          <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300" />
                        </div>
                      </div>

                      {/* Quick Actions Row (visible on hover/tap) */}
                      {isNew && (
                        <div className="mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDismiss(alert);
                            }}
                            className="flex-1 h-8 text-xs text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            <EyeOff className="h-3 w-3 mr-1" />
                            Dismiss
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcknowledge(alert);
                            }}
                            className="flex-1 h-8 text-xs bg-yellow-500 hover:bg-yellow-600 text-white"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Acknowledge
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 p-4 sm:p-6 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Showing {filteredAlerts.length} of {alerts.length} alerts
              </div>
              <Button
                onClick={onClose}
                className="w-full sm:w-auto h-11 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900 font-medium"
              >
                Close Alert Center
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
